import { useTranslation } from 'react-i18next';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts/core';
import { BarChart, LineChart } from 'echarts/charts';
import Hidden from 'raydiant-elements/layout/Hidden';
import { ContentDetailSummary } from '@raydiant/api-client-js';
import { renderToStaticMarkup } from 'react-dom/server';

import ChartCard from '../ChartCard/ChartCard';
import {
  CHART_DEFAULT_AXIS_OPTIONS,
  CHART_DEFAULT_OPTIONS,
  CHART_DEFAULT_SERIES,
} from '../utils';
import ChartToolTip, { EChartsToolTipParams } from '../ChartToolTip';
import { colorsForContentDetailCharts } from './utils';

echarts.use([BarChart, LineChart]);

type AverageContentChartProps = {
  contentData: ContentDetailSummary;
};

const color = colorsForContentDetailCharts;

const AverageContentChart = ({ contentData }: AverageContentChartProps) => {
  const { t } = useTranslation();

  const isEmptyData =
    !contentData.allAvgViewDuration &&
    !contentData.allAvgVerifiedViewsRate &&
    !contentData.allAvgEngagedViewsRate &&
    !contentData.avgViewDuration &&
    !contentData.verifiedViewsRate &&
    !contentData.engagedViewsRate;

  const getXAxisDataRate = () => [
    t(
      'analytics.contentDetailPage.averageContent.viewRateChart.verifiedViewLabel',
    ),
    t(
      'analytics.contentDetailPage.averageContent.viewRateChart.engagedViewLabel',
    ),
  ];
  const getXAxisDataTime = () => [
    t(
      'analytics.contentDetailPage.averageContent.viewTimeChart.averageViewTime',
    ),
  ];

  const getYAxisData = (
    data: ContentDetailSummary,
    view: keyof ContentDetailSummary,
  ) => data[view];

  const getSeriesRate = (data: ContentDetailSummary) => {
    let series = [];

    series.push({
      ...CHART_DEFAULT_SERIES.bar,
      data: [
        getYAxisData(data, 'verifiedViewsRate'),
        getYAxisData(data, 'engagedViewsRate'),
      ],
      name: contentData.presentationName,
      stack: 'viewsRate',
      itemStyle: {
        borderRadius: [15, 15, 0, 0],
      },
      barWidth: '30%',
    });

    series.push({
      ...CHART_DEFAULT_SERIES.bar,
      data: [
        getYAxisData(data, 'allAvgVerifiedViewsRate'),
        getYAxisData(data, 'allAvgEngagedViewsRate'),
      ],
      name: t(
        'analytics.contentDetailPage.averageContent.viewRateChart.averageRateLegend',
      ),
      stack: 'averageViewsRate',
      itemStyle: {
        borderRadius: [15, 15, 0, 0],
      },
      barWidth: '30%',
    });
    return series;
  };

  const getSeriesTime = (data: ContentDetailSummary) => {
    let series = [];

    series.push({
      ...CHART_DEFAULT_SERIES.bar,
      data: [getYAxisData(data, 'avgViewDuration')],
      name: contentData.presentationName,
      stack: 'viewDuration',
      itemStyle: {
        borderRadius: [15, 15, 0, 0],
      },
      barWidth: '17%',
    });

    series.push({
      ...CHART_DEFAULT_SERIES.bar,
      data: [getYAxisData(data, 'allAvgViewDuration')],
      name: t(
        'analytics.contentDetailPage.averageContent.viewTimeChart.averageTimeLegend',
      ),
      stack: 'averageViewDuration',
      itemStyle: {
        borderRadius: [15, 15, 0, 0],
      },
      barWidth: '17%',
    });

    return series;
  };

  const optionsRate = {
    ...CHART_DEFAULT_OPTIONS,
    color,
    grid: {
      ...CHART_DEFAULT_OPTIONS.grid,
      bottom: -25,
      top: 70,
    },
    legend: {
      ...CHART_DEFAULT_OPTIONS.legend,
      data: [
        {
          name: contentData.presentationName,
          icon: 'path://M2,2 h16 a6,6 0 0 1 6,6 v16 a6,6 0 0 1 -6,6 h-16 a6,6 0 0 1 -6,-6 v-16 a6,6 0 0 1 6,-6 z',
        },
        {
          name: t(
            'analytics.contentDetailPage.averageContent.viewRateChart.averageRateLegend',
          ),
          icon: 'path://M2,2 h16 a6,6 0 0 1 6,6 v16 a6,6 0 0 1 -6,6 h-16 a6,6 0 0 1 -6,-6 v-16 a6,6 0 0 1 6,-6 z',
        },
      ],
    },
    xAxis: [
      {
        ...CHART_DEFAULT_AXIS_OPTIONS.xAxis,
        data: getXAxisDataRate(),
        axisLabel: {
          ...CHART_DEFAULT_AXIS_OPTIONS.xAxis.axisLabel,
          width: 150,
        },
      },
    ],
    yAxis: [
      {
        ...CHART_DEFAULT_AXIS_OPTIONS.yAxis,
        alignTicks: true,
        name: `${t('analytics.engagement').toUpperCase()} (%)`,
        axisLabel: {
          ...CHART_DEFAULT_AXIS_OPTIONS.yAxis.axisLabel,
        },
        interval: 20,
        splitLine: {
          show: true,
          lineStyle: {
            type: 'solid',
            color: '#FDF0F0',
            width: 2,
          },
        },
      },
    ],
    tooltip: {
      ...CHART_DEFAULT_OPTIONS.tooltip,
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      formatter: (params: EChartsToolTipParams[]) => {
        const modifiedParams = params.map((item) => ({
          ...item,
          value: item.value ? `${Number(item.value).toFixed(2)}%` : '-',
        }));

        return renderToStaticMarkup(
          <ChartToolTip
            header={
              <p className="font-bold text-sm px-2 py-1 text-black">
                {params[0].axisValueLabel}
              </p>
            }
            values={modifiedParams}
          />,
        );
      },
    },
    series: getSeriesRate(contentData),
  };

  const optionsTime = {
    ...CHART_DEFAULT_OPTIONS,
    color,
    grid: {
      ...CHART_DEFAULT_OPTIONS.grid,
      bottom: -25,
      top: 70,
      left: 70,
    },
    legend: {
      ...CHART_DEFAULT_OPTIONS.legend,
      data: [
        {
          name: contentData.presentationName,
          icon: 'path://M2,2 h16 a6,6 0 0 1 6,6 v16 a6,6 0 0 1 -6,6 h-16 a6,6 0 0 1 -6,-6 v-16 a6,6 0 0 1 6,-6 z',
        },
        {
          name: t(
            'analytics.contentDetailPage.averageContent.viewTimeChart.averageTimeLegend',
          ),
          icon: 'path://M2,2 h16 a6,6 0 0 1 6,6 v16 a6,6 0 0 1 -6,6 h-16 a6,6 0 0 1 -6,-6 v-16 a6,6 0 0 1 6,-6 z',
        },
      ],
    },
    xAxis: [
      {
        ...CHART_DEFAULT_AXIS_OPTIONS.xAxis,
        data: getXAxisDataTime(),
        axisLabel: {
          ...CHART_DEFAULT_AXIS_OPTIONS.xAxis.axisLabel,
          width: 150,
        },
      },
    ],
    yAxis: [
      {
        ...CHART_DEFAULT_AXIS_OPTIONS.yAxis,
        alignTicks: true,
        name: `${t(
          'analytics.contentDetailPage.tableHeaders.avgViewDuration',
        )} \n(sec)`.toUpperCase(),
        axisLabel: {
          ...CHART_DEFAULT_AXIS_OPTIONS.yAxis.axisLabel,
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: 'solid',
            color: '#FDF0F0',
            width: 2,
          },
        },
      },
    ],
    tooltip: {
      ...CHART_DEFAULT_OPTIONS.tooltip,
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      formatter: (params: EChartsToolTipParams[]) => {
        const modifiedParams = params.map((item) => ({
          ...item,
          value: item.value ? `${Number(item.value).toFixed(2)} sec` : '-',
        }));

        return renderToStaticMarkup(
          <ChartToolTip
            header={
              <p className="font-bold text-sm px-2 py-1 text-black">
                {params[0].axisValueLabel}
              </p>
            }
            values={modifiedParams}
          />,
        );
      },
    },
    series: getSeriesTime(contentData),
  };

  const mobileOptionsRate = {
    ...optionsRate,
    yAxis: [
      {
        ...optionsRate.yAxis[0],
        nameTextStyle: {
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 12,
          height: 70,
        },
      },
    ],
    xAxis: [
      {
        ...optionsRate.xAxis[0],
        axisLabel: {
          ...CHART_DEFAULT_AXIS_OPTIONS.xAxis.axisLabel,
          width: 150,
          fontSize: 12,
        },
      },
    ],
    legend: {
      ...CHART_DEFAULT_OPTIONS.legend,
      data: [
        {
          name: contentData.presentationName,
          icon: 'path://M2,2 h16 a6,6 0 0 1 6,6 v16 a6,6 0 0 1 -6,6 h-16 a6,6 0 0 1 -6,-6 v-16 a6,6 0 0 1 6,-6 z',
        },
        {
          name: t(
            'analytics.contentDetailPage.averageContent.viewRateChart.averageRateLegend',
          ),
          icon: 'path://M2,2 h16 a6,6 0 0 1 6,6 v16 a6,6 0 0 1 -6,6 h-16 a6,6 0 0 1 -6,-6 v-16 a6,6 0 0 1 6,-6 z',
        },
      ],
      type: 'scroll',
    },
    tooltip: {
      ...CHART_DEFAULT_OPTIONS.tooltip,
      trigger: 'item',
      position: 'right',
      formatter: (params: EChartsToolTipParams) => {
        const modifiedParams = {
          ...params,
          value: params.value ? `${Number(params.value).toFixed(2)}%` : '-',
        };

        return renderToStaticMarkup(
          <ChartToolTip
            header={
              <p className="font-bold text-sm px-2 py-1 text-black">
                {params.name}
              </p>
            }
            values={[modifiedParams]}
          />,
        );
      },
    },
    series: getSeriesRate(contentData),
  };

  const mobileOptionsTime = {
    ...optionsTime,
    grid: {
      ...CHART_DEFAULT_OPTIONS.grid,
      bottom: -25,
      top: 70,
      left: 45,
    },
    yAxis: [
      {
        ...optionsTime.yAxis[0],
        nameTextStyle: {
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 12,
          height: 70,
        },
      },
    ],
    xAxis: [
      {
        ...optionsTime.xAxis[0],
        axisLabel: {
          ...CHART_DEFAULT_AXIS_OPTIONS.xAxis.axisLabel,
          width: 150,
          fontSize: 12,
        },
      },
    ],
    legend: {
      ...CHART_DEFAULT_OPTIONS.legend,
      data: [
        {
          name: contentData.presentationName,
          icon: 'path://M2,2 h16 a6,6 0 0 1 6,6 v16 a6,6 0 0 1 -6,6 h-16 a6,6 0 0 1 -6,-6 v-16 a6,6 0 0 1 6,-6 z',
        },
        {
          name: t(
            'analytics.contentDetailPage.averageContent.viewTimeChart.averageTimeLegend',
          ),
          icon: 'path://M2,2 h16 a6,6 0 0 1 6,6 v16 a6,6 0 0 1 -6,6 h-16 a6,6 0 0 1 -6,-6 v-16 a6,6 0 0 1 6,-6 z',
        },
      ],
      type: 'scroll',
    },
    tooltip: {
      ...CHART_DEFAULT_OPTIONS.tooltip,
      trigger: 'item',
      position: 'left',
      formatter: (params: EChartsToolTipParams) => {
        const modifiedParams = {
          ...params,
          value: params.value ? `${Number(params.value).toFixed(2)} sec` : '-',
        };

        return renderToStaticMarkup(
          <ChartToolTip
            header={
              <p className="font-bold text-sm px-2 py-1 text-black">
                {params.name}
              </p>
            }
            values={[modifiedParams]}
          />,
        );
      },
    },
    series: getSeriesTime(contentData),
  };

  return (
    <ChartCard subTitle={''} title={''} showTimeInterval={false}>
      {isEmptyData ? (
        <div className="w-full text-center">
          {t('analytics.contentDetailPage.emptyData.emptyChartData')}
        </div>
      ) : (
        <div className="w-full min-h-full flex items-center justify-around flex-wrap sm:flex-nowrap">
          <Hidden xsDown>
            <ReactECharts
              style={{ height: '370px', width: '50%' }}
              option={optionsRate}
              onChartReady={(chart) => chart.resize()}
            />
            <ReactECharts
              style={{ height: '370px', width: '50%' }}
              option={optionsTime}
              onChartReady={(chart) => chart.resize()}
            />
          </Hidden>
          <Hidden smUp>
            <ReactECharts
              style={{ height: '370px', width: '100%' }}
              option={mobileOptionsRate}
              onChartReady={(chart) => chart.resize()}
            />
            <ReactECharts
              style={{ height: '370px', width: '100%', marginTop: '20px' }}
              option={mobileOptionsTime}
              onChartReady={(chart) => chart.resize()}
            />
          </Hidden>
        </div>
      )}
    </ChartCard>
  );
};

export default AverageContentChart;
